import { useState, useEffect, useRef } from 'react'
import { useTranslation } from '@papan/i18n'

// ---------------------------------------------------------------------------
// Shared navigation chrome for the full-screen "settings-like" surfaces —
// SettingsView and BudgetBillingView. Extracted so both render an identical
// sidebar + "On this page" outline without duplicating ~150 lines.
// ---------------------------------------------------------------------------

/** A sidebar nav row. Desktop: full-width vertical row; mobile: a shrink-0
 *  chip in the horizontal nav strip. */
export function SidebarItem({
  icon: Icon,
  label,
  active,
  onClick,
  mobile,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>
  label: string
  active: boolean
  onClick: () => void
  mobile?: boolean
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors ${
        mobile ? 'shrink-0 whitespace-nowrap border themed-border' : 'w-full'
      } ${
        active
          ? 'themed-bg-tertiary themed-text font-medium'
          : 'themed-text-muted hover:themed-text hover:themed-bg-tertiary'
      }`}
    >
      <Icon size={16} />
      {label}
    </button>
  )
}

/** A sidebar section header. Hidden on mobile — section headers don't fit a
 *  horizontal chip strip. */
export function SidebarSection({ label, mobile }: { label: string; mobile?: boolean }) {
  if (mobile) return null
  return (
    <div className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider themed-text-muted">
      {label}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ScrollSpy — right-hand "On this page" outline (desktop only). Indexes the
// h2/h3 headings of the currently rendered page and tracks which one is in
// view as the content column scrolls.
// ---------------------------------------------------------------------------

interface SpyHeading { id: string; text: string; level: number }

/**
 * A heading's outline label — its text with any `[data-toc-ignore]` descendant
 * removed. A heading may carry inline chrome that is not part of its name (a
 * count badge, a status pill), and a naive `textContent` folds it in, so
 * "Unpaid tenants" beside a "2" badge indexed as "Unpaid tenants2". Anything
 * placed inside an h2/h3 that is not the title marks itself `data-toc-ignore`.
 */
function headingLabel(el: HTMLElement): string {
  if (!el.querySelector('[data-toc-ignore]')) return (el.textContent ?? '').trim()
  const clone = el.cloneNode(true) as HTMLElement
  clone.querySelectorAll('[data-toc-ignore]').forEach(n => n.remove())
  return (clone.textContent ?? '').trim()
}

export function ScrollSpy({ containerRef, pageKey, idPrefix = 'settings-spy' }: {
  /** The scrollable body container the headings live in. */
  containerRef: React.RefObject<HTMLDivElement | null>
  /** Changes when the page changes — re-collects immediately. */
  pageKey: string
  /**
   * Namespace for the generated heading ids. Element ids are DOCUMENT-global
   * but the counter below is per-instance, so two ScrollSpys alive at once both
   * mint `…-0`, `…-1`, … — and the Settings and Usage & Billing decks are both
   * kept alive (one merely hidden). That produced duplicate React keys in the
   * outline and let `getElementById` resolve a click to the HIDDEN deck's
   * heading. Each mount passes its own prefix.
   *
   * This handles collisions ACROSS decks only. The within-deck half of the same
   * bug is handled by `nextId` — see the note there.
   */
  idPrefix?: string
}) {
  const { t } = useTranslation('core-ui')
  const [headings, setHeadings] = useState<SpyHeading[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  /**
   * Monotonic id counter — NOT the array index.
   *
   * Indexing by position looks equivalent and is not: ids are minted only for
   * elements that lack one, so a heading inserted ABOVE an already-numbered one
   * takes an index that has already been handed out. On the Billing page
   * `CreditsSection` loads async and mounts between "Change plan" and "Invoice
   * history", so the first pass numbered Invoice history `-2` and the second
   * gave the same `-2` to Usage credits — two headings, one id. That is the
   * duplicate React key and the `getElementById`-resolves-the-wrong-heading bug
   * from finding #12, which `idPrefix` fixed only ACROSS decks; this is the
   * same defect WITHIN one deck. A counter that only ever increments cannot
   * collide, whatever order things mount in.
   */
  const nextId = useRef(0)

  // Collect headings from the rendered page. Pages load data async (and some
  // are render props resolved elsewhere), so a MutationObserver keeps the
  // outline in sync — childList/characterData only, NOT attributes, because
  // the id assignment below is an attribute write and must not re-trigger
  // the observer. Headings inside inline fixed overlays (modals) are skipped.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    let raf = 0
    const collect = () => {
      const els = Array.from(container.querySelectorAll<HTMLElement>('h2, h3'))
        .filter(el => !el.closest('.fixed') && headingLabel(el))
      els.forEach(el => { if (!el.id) el.id = `${idPrefix}-${nextId.current++}` })
      const next = els.map(el => ({
        id: el.id,
        text: headingLabel(el),
        level: el.tagName === 'H2' ? 2 : 3,
      }))
      setHeadings(prev =>
        prev.length === next.length && prev.every((h, i) => h.id === next[i].id && h.text === next[i].text)
          ? prev
          : next
      )
    }
    collect()
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(collect)
    })
    mo.observe(container, { childList: true, subtree: true, characterData: true })
    return () => { mo.disconnect(); cancelAnimationFrame(raf) }
  }, [containerRef, pageKey, idPrefix])

  // Track the heading currently at/above the top of the scrollport.
  useEffect(() => {
    const container = containerRef.current
    if (!container || headings.length === 0) { setActiveId(null); return }
    let raf = 0
    const update = () => {
      const cTop = container.getBoundingClientRect().top
      let current = headings[0].id
      for (const h of headings) {
        const el = document.getElementById(h.id)
        if (el && el.getBoundingClientRect().top - cTop <= 40) current = h.id
      }
      setActiveId(current)
    }
    update()
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update) }
    container.addEventListener('scroll', onScroll, { passive: true })
    return () => { container.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf) }
  }, [containerRef, headings])

  const jump = (id: string) => {
    const container = containerRef.current
    const el = document.getElementById(id)
    if (!container || !el) return
    const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop
    container.scrollTo({ top: Math.max(0, top - 16), behavior: 'smooth' })
    setActiveId(id)
  }

  // A one-heading outline is noise — most pages have a single h2 title.
  if (headings.length < 2) return null

  return (
    <aside
      data-scroll-skip
      // `xl`, not `lg`. At lg (1024) this still claimed 160px of a window
      // whose content column was only 482px — a navigation aid out-competing
      // the thing it navigates. Below 1280 the outline is a convenience and
      // the table is the work.
      className="w-[160px] shrink-0 py-7 sticky top-0 self-start max-h-[100cqh] overflow-y-auto hidden xl:block"
      aria-label={t('settings.onThisPage')}
    >
      <div className="pl-3 pb-1 text-[10px] font-semibold uppercase tracking-wider themed-text-muted">
        {t('settings.onThisPage')}
      </div>
      {headings.map(h => (
        <button
          key={h.id}
          onClick={() => jump(h.id)}
          className={`block w-full text-left text-xs py-1 border-l-2 transition-colors ${
            h.level === 3 ? 'pl-5' : 'pl-3'
          } ${
            activeId === h.id
              ? 'themed-text border-blue-500 font-medium'
              : 'themed-text-muted hover:themed-text border-transparent'
          }`}
        >
          {h.text}
        </button>
      ))}
    </aside>
  )
}
